import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import api from '../services/api';
import Dialog from '../components/Dialog';
import { useDialog } from '../hooks/useDialog';
import { Activity, Bell, ShieldCheck, HelpCircle, ChevronLeft, Check, AlertCircle } from 'lucide-react';
import './ObservabilityIntegration.css';

const ObservabilityIntegration = () => {
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const { dialogState, closeDialog, alert: customAlert } = useDialog();
    const [loading, setLoading] = useState(true);
    const [connected, setConnected] = useState(false);
    const [integrationId, setIntegrationId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);

    const [settings, setSettings] = useState({
        otelEnabled: false,
        otelEndpoint: '',
        otelHeaders: '',
        grafanaUrl: '',
        kibanaUrl: '',
        customUrl: '',
        customLabel: '',
    });

    useEffect(() => {
        if (currentWorkspace) {
            checkStatus();
        }
    }, [currentWorkspace]);

    const checkStatus = async () => {
        setLoading(true);
        try {
            const response = await api.get('/integrations/observability/status', {
                params: { workspaceId: currentWorkspace.id }
            });

            if (response.data.connected) {
                setConnected(true);
                setIntegrationId(response.data.id);
                if (response.data.settings) {
                    const s = response.data.settings;
                    setSettings({
                        otelEnabled: s.otelEnabled || false,
                        otelEndpoint: s.otelEndpoint || '',
                        otelHeaders: s.otelHeaders || '',
                        grafanaUrl: s.grafanaUrl || '',
                        kibanaUrl: s.kibanaUrl || '',
                        customUrl: s.customUrl || '',
                        customLabel: s.customLabel || '',
                    });
                }
            } else {
                setConnected(false);
            }
        } catch (error) {
            console.error('Error checking Observability status:', error);
            setConnected(false);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.post('/integrations/observability/save', {
                workspaceId: currentWorkspace.id,
                settings
            });
            setConnected(true);
            customAlert({
                title: 'Settings Saved',
                message: 'Observability and OpenTelemetry integration settings have been updated successfully.',
                variant: 'success'
            });
        } catch (error) {
            console.error('Error saving Observability settings:', error);
            customAlert({
                title: 'Save Failed',
                message: error.response?.data?.error || 'Failed to save settings. Please try again.',
                variant: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setIsTesting(true);
        try {
            const response = await api.post('/integrations/observability/test', {
                workspaceId: currentWorkspace.id,
                settings
            });
            customAlert({
                title: 'Test Successful',
                message: response.data.message || 'Validation trace sent to collector!',
                variant: 'success'
            });
        } catch (err) {
            customAlert({
                title: 'Test Failed',
                message: err.response?.data?.error || 'Failed to send test span. Please check endpoint and configuration.',
                variant: 'error'
            });
        } finally {
            setIsTesting(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            await api.delete('/integrations/observability/disconnect', {
                data: { workspaceId: currentWorkspace.id }
            });
            setConnected(false);
            setSettings({
                otelEnabled: false,
                otelEndpoint: '',
                otelHeaders: '',
                grafanaUrl: '',
                kibanaUrl: '',
                customUrl: '',
                customLabel: '',
            });
            customAlert({
                title: 'Disconnected',
                message: 'Observability integration disconnected.',
                variant: 'success'
            });
        } catch (error) {
            console.error('Error disconnecting Observability:', error);
            customAlert({
                title: 'Disconnect Failed',
                message: 'Failed to disconnect integration. Please try again.',
                variant: 'error'
            });
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    if (loading) {
        return (
            <div className="observability-integration-page loading-state">
                <div className="spinner"></div>
                <p>Loading Observability settings...</p>
            </div>
        );
    }

    return (
        <div className="observability-integration-page">
            <div className="page-header-navigation">
                <button className="back-btn" onClick={() => navigate('/settings/integrations')}>
                    <ChevronLeft size={20} /> Back to Integrations
                </button>
            </div>

            <div className="observability-container">
                <div className="observability-header">
                    <div className="header-icon-container">
                        <Activity size={32} color="#10b981" />
                    </div>
                    <div className="header-text-container">
                        <h1>Observability & Monitoring</h1>
                        <p className="subtitle">
                            Link dashboard templates and export mobile test execution traces via OpenTelemetry (OTLP).
                        </p>
                    </div>
                    {connected && (
                        <div className="status-badge active-status">
                            <span className="pulse-dot"></span> Connected
                        </div>
                    )}
                </div>

                <div className="observability-grid">
                    <div className="main-config-panel card-panel">
                        <h2>Telemetry Exporter Settings</h2>
                        <p className="description">
                            Send traces directly from the execution runner to your backend APM or tracing system.
                        </p>

                        <div className="form-group toggle-group">
                            <label className="toggle-label">
                                <input
                                    type="checkbox"
                                    name="otelEnabled"
                                    checked={settings.otelEnabled}
                                    onChange={handleChange}
                                />
                                <span className="toggle-slider"></span>
                                <div className="toggle-text">
                                    <span className="title">Enable OpenTelemetry Trace Export</span>
                                    <span className="hint">Direct trace outputs to your OTLP collector.</span>
                                </div>
                            </label>
                        </div>

                        {settings.otelEnabled && (
                            <div className="otel-sub-form animate-fade-in">
                                <div className="form-group">
                                    <label htmlFor="otelEndpoint">OTLP Exporter Endpoint (HTTP/JSON)</label>
                                    <input
                                        type="text"
                                        id="otelEndpoint"
                                        name="otelEndpoint"
                                        value={settings.otelEndpoint}
                                        onChange={handleChange}
                                        placeholder="http://localhost:4318/v1/traces"
                                        className="form-control"
                                    />
                                    <span className="input-hint">Must be the HTTP OTLP path (usually ending in /v1/traces).</span>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="otelHeaders">OTLP Headers (Key=Value, one per line)</label>
                                    <textarea
                                        id="otelHeaders"
                                        name="otelHeaders"
                                        value={settings.otelHeaders}
                                        onChange={handleChange}
                                        placeholder="Authorization=Bearer token&#10;Custom-Header=value"
                                        className="form-control text-area"
                                        rows={3}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="form-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={handleTest}
                                disabled={isTesting}
                            >
                                {isTesting ? 'Sending Trace...' : 'Test Connection'}
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Configuration'}
                            </button>
                            {connected && (
                                <button className="btn btn-danger-link" onClick={handleDisconnect}>
                                    Disconnect Integration
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="dashboards-panel card-panel">
                        <h2>Dashboard Templates</h2>
                        <p className="description">
                            Provide URLs to your Grafana, Kibana, or other APM platforms. Dynamic variable <code>{"{appId}"}</code> will automatically resolve on your app pages.
                        </p>

                        <div className="form-group">
                            <label htmlFor="grafanaUrl">Grafana Dashboard URL Template</label>
                            <input
                                type="text"
                                id="grafanaUrl"
                                name="grafanaUrl"
                                value={settings.grafanaUrl}
                                onChange={handleChange}
                                placeholder="https://grafana.example.com/d/abc?var-app={appId}"
                                className="form-control"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="kibanaUrl">Kibana Discover URL Template</label>
                            <input
                                type="text"
                                id="kibanaUrl"
                                name="kibanaUrl"
                                value={settings.kibanaUrl}
                                onChange={handleChange}
                                placeholder="https://kibana.example.com/app/discover#/?_a=(query:(language:kuery,query:'app_id:&quot;{appId}&quot;'))"
                                className="form-control"
                            />
                        </div>

                        <div className="form-group-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label htmlFor="customUrl">Custom Dashboard URL Template</label>
                                <input
                                    type="text"
                                    id="customUrl"
                                    name="customUrl"
                                    value={settings.customUrl}
                                    onChange={handleChange}
                                    placeholder="https://signoz.example.com/dashboard/{appId}"
                                    className="form-control"
                                />
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label htmlFor="customLabel">Custom Label</label>
                                <input
                                    type="text"
                                    id="customLabel"
                                    name="customLabel"
                                    value={settings.customLabel}
                                    onChange={handleChange}
                                    placeholder="SigNoz"
                                    className="form-control"
                                />
                            </div>
                        </div>

                        <div className="info-box-premium">
                            <HelpCircle size={18} color="#10b981" />
                            <div>
                                <h4>How variables work</h4>
                                <p>Use <code>{"{appId}"}</code> inside your URLs. On each App page, we will render links direct to these dashboards with the current app's context replaced.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                isOpen={dialogState.isOpen}
                onClose={closeDialog}
                title={dialogState.title}
                message={dialogState.message}
                variant={dialogState.variant}
            />
        </div>
    );
};

export default ObservabilityIntegration;
